/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Zustand store for app persistence
 */
// TODO - check local storage persistence through zustand
import { create } from 'zustand';
import _ from 'lodash';
import { getValueFromStorage, setValueToStorage } from 'utils/common';
import { devtools } from 'zustand/middleware';

const defaultFormValues = {
	npi: '',
	payer: '',
	serviceType: [],
	eligibilityPersistedCombinations: {
		patient: {},
		provider: {},
	},
	claimPersistedCombinations: {
		patient: {},
		provider: {},
	},
};

const logicValues = {
	formValues: { key: 'formValues', default: defaultFormValues },
	loginCount: { key: 'loginCount', default: 0 },
	providerAlertKeys: { key: 'providerAlertKeys', default: [] },
	usageAlertBarKey: { key: 'usageAlertBarKey', default: '' },
	hasVisitedBatchModal: { key: 'hasVisitedBatchModal', default: false },
	specialNotificationLastSeen: { key: 'specialNotificationLastSeen', default: '' },
	codeExplorerTutorialLastSeen: { key: 'codeExplorerTutorialLastSeen', default: '' },
	failedInvoiceId: { key: 'failedInvoiceId', default: '' },
};

const initialState = {
	allowNavigation: true,
	[logicValues.formValues.key]: getValueFromStorage(logicValues.formValues.key, logicValues.formValues.default),
	[logicValues.loginCount.key]: getValueFromStorage(logicValues.loginCount.key, logicValues.loginCount.default),
	[logicValues.providerAlertKeys.key]: getValueFromStorage(logicValues.providerAlertKeys.key, logicValues.providerAlertKeys.default),
	[logicValues.usageAlertBarKey.key]: getValueFromStorage(logicValues.usageAlertBarKey.key, logicValues.usageAlertBarKey.default),
	[logicValues.hasVisitedBatchModal.key]: getValueFromStorage(
		logicValues.hasVisitedBatchModal.key,
		logicValues.hasVisitedBatchModal.default,
	),
	serviceStartDate: '',
	serviceEndDate: '',
	[logicValues.specialNotificationLastSeen.key]: getValueFromStorage(
		logicValues.specialNotificationLastSeen.key,
		logicValues.specialNotificationLastSeen.default,
	),
	[logicValues.codeExplorerTutorialLastSeen.key]: getValueFromStorage(
		logicValues.codeExplorerTutorialLastSeen.key,
		logicValues.codeExplorerTutorialLastSeen.default,
	),
	[logicValues.failedInvoiceId.key]: getValueFromStorage(logicValues.failedInvoiceId.key, logicValues.failedInvoiceId.default),
} as Persistence.IValues;

const usePersistenceStore = create<Persistence.IStore>()(
	devtools(
		(set) => ({
			...initialState,
			actions: {
				setAllowNavigation: (allowNavigation) => set(() => ({ allowNavigation })),
				setFormValues: (formValues) =>
					set(() => {
						const newValue = formValues || logicValues.formValues.default;
						setValueToStorage(logicValues.formValues.key, newValue);
						return { [logicValues.formValues.key]: newValue };
					}),

				setLoginCount: (loginCount) =>
					set(() => {
						const newValue = loginCount || logicValues.loginCount.default;
						setValueToStorage(logicValues.loginCount.key, newValue);
						return { [logicValues.loginCount.key]: newValue };
					}),

				setProviderAlertKeys: (providerAlertKeys) =>
					set(() => {
						const newValue = providerAlertKeys || logicValues.providerAlertKeys.default;
						setValueToStorage(logicValues.providerAlertKeys.key, newValue);
						return { [logicValues.providerAlertKeys.key]: newValue };
					}),

				setUsageAlertBarKey: (usageAlertBarKey) =>
					set(() => {
						const newValue = usageAlertBarKey || logicValues.usageAlertBarKey.default;
						setValueToStorage(logicValues.usageAlertBarKey.key, newValue);
						return { [logicValues.usageAlertBarKey.key]: newValue };
					}),

				setHasVisitedBatchModal: (hasVisitedBatchModal) =>
					set(() => {
						const newValue = hasVisitedBatchModal || logicValues.hasVisitedBatchModal.default;
						setValueToStorage(logicValues.hasVisitedBatchModal.key, newValue);
						return { [logicValues.hasVisitedBatchModal.key]: newValue };
					}),

				setServiceStartDate: (serviceStartDate) => set(() => ({ serviceStartDate })),

				setServiceEndDate: (serviceEndDate) => set(() => ({ serviceEndDate })),

				setSpecialNotificationLastSeen: (specialNotificationLastSeen) =>
					set(() => {
						const newValue = specialNotificationLastSeen || logicValues.specialNotificationLastSeen.default;
						setValueToStorage(logicValues.specialNotificationLastSeen.key, newValue);
						return { [logicValues.specialNotificationLastSeen.key]: newValue };
					}),

				setCodeExplorerTutorialLastSeen: (codeExplorerTutorialLastSeen) =>
					set(() => {
						const newValue = codeExplorerTutorialLastSeen || logicValues.codeExplorerTutorialLastSeen.default;
						setValueToStorage(logicValues.codeExplorerTutorialLastSeen.key, newValue);
						return { [logicValues.codeExplorerTutorialLastSeen.key]: newValue };
					}),

				setFailedInvoiceId: (failedInvoiceId) =>
					set(() => {
						const newValue = failedInvoiceId || logicValues.failedInvoiceId.default;
						setValueToStorage(logicValues.failedInvoiceId.key, newValue);
						return { [logicValues.failedInvoiceId.key]: newValue };
					}),
			},
		}),
		{ name: 'Persistence Store' },
	),
);

// Store to return actions and values
const usePersistenceStoreValues = (): Persistence.IValues => usePersistenceStore((state) => _.omit(state, ['actions']));
const usePersistenceStoreActions = (): Persistence.IActions => usePersistenceStore((state) => state.actions);

export { usePersistenceStoreValues, usePersistenceStoreActions };
export default usePersistenceStore;
